'use client'

import { clsx } from 'clsx'
import { type FC, type ReactNode, useCallback, useRef } from 'react'

import { useGlobalConfigContext } from '../../../../../global-config'
import { NextLink } from '../../../../../next-link'
import { urlResolverGetPathForPimProduct } from '../../../../../url-handling'
import { type SelectionEntry } from '../../../product-page-context/AttributeGroup'
import { useProductPageContext } from '../../../productPageContext'
import { useMobileScrollAttributeIntoViewOnMount } from '../useMobileScrollAttributeIntoViewOnMount'
import { type ScrollToElement } from '../useScrollElementIntoView'

import { productPageGetAttributeSelectionEntryConditions } from './productPageGetAttributeSelectionEntryConditions'

interface ProductPageAttributeSelectorProps {
  className?: string
  error?: boolean
  label: ReactNode
  onScrollIntoView?: ScrollToElement
  selectionEntry: SelectionEntry
}

export const ProductPageAttributeSelector: FC<
  ProductPageAttributeSelectorProps
> = ({ className, error, label, onScrollIntoView, selectionEntry }) => {
  const globalConfig = useGlobalConfigContext()
  const { attributeSelectionControls } = useProductPageContext()
  const pillElementRef = useRef(null)

  const conditions =
    productPageGetAttributeSelectionEntryConditions(selectionEntry)
  const {
    candidateIsNotBuyable,
    candidateVariant,
    notSelectedAndBuyableVariants,
    notSelectedAndNoVariants,
    selectedAndHasVariants,
    selectedButNotBuyable,
    selectedButNoVariants,
    showAsLink,
  } = conditions

  useMobileScrollAttributeIntoViewOnMount({
    attributeElement: pillElementRef,
    isVariantSelected: selectedAndHasVariants,
    onScrollIntoView,
  })

  const handleAttributeSelection = useCallback((): void => {
    attributeSelectionControls.handleAttributeSelect(selectionEntry)
  }, [attributeSelectionControls, selectionEntry])

  const pillClassName = clsx(
    'max-w-full shrink-0 truncate rounded-full border border-transparent px-2.5 py-1.5 text-start scrollbar-hide',
    selectedAndHasVariants && 'bg-dark-brand text-light-primary-strong',
    (notSelectedAndNoVariants || candidateIsNotBuyable) &&
      'bg-light-primary-high text-dark-primary-strong',
    notSelectedAndBuyableVariants && 'bg-light-primary-low text-dark-brand',
    (selectedButNoVariants || selectedButNotBuyable) &&
      'border-dark-brand bg-light-primary-high text-dark-primary-strong',
    error && 'border-dark-error',
  )

  if (showAsLink && candidateVariant) {
    return (
      <NextLink
        className={clsx(pillClassName, className)}
        data-qa-id="offer-attribute-tile"
        href={urlResolverGetPathForPimProduct(globalConfig, candidateVariant)}
        onClick={handleAttributeSelection}
        ref={pillElementRef}
        scroll={false}
        title={selectionEntry.attribute.value}
      >
        {label}
      </NextLink>
    )
  }

  return (
    <button
      className={clsx(pillClassName, className)}
      onClick={handleAttributeSelection}
      ref={pillElementRef}
      title={selectionEntry.attribute.value}
      type="button"
    >
      {label}
    </button>
  )
}
